import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isInsideCommentsUI, positionFor, UI_ATTR } from './anchor';
import { Comment } from './types';
import { useComments } from './useCommentsState';
import { ThreadPanel } from './ThreadPanel';
import { Composer } from './Composer';

export function PinLayer() {
  const {
    routeComments,
    activeThreadId,
    highlightThreadId,
    draft,
    author,
    drawerOpen,
    openThread,
    submitReply,
    resolveThread,
    deleteComment,
    submitNew,
    exitCommentMode,
  } = useComments();

  // tick força recomputo das posições (scroll/resize/DOM).
  const [, setTick] = useState(0);
  const bump = useRef<() => void>(() => {});
  bump.current = () => setTick((t) => (t + 1) % 1000000);

  useEffect(() => {
    let raf = 0;
    const onScrollResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => bump.current());
    };
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);

    let debounce: ReturnType<typeof setTimeout>;
    const onMutate = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => bump.current(), 120);
    };
    const ro = new ResizeObserver(onMutate);
    ro.observe(document.body);
    // childList apenas (sem attributes → não dispara em hover/transição), e
    // ignora mutações que acontecem dentro da própria UI de comentários.
    const mo = new MutationObserver((mutations) => {
      if (mutations.some((m) => !isInsideCommentsUI(m.target as Element))) onMutate();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
      cancelAnimationFrame(raf);
      clearTimeout(debounce);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  // Threads raiz abertas (não resolvidas), numeradas por ordem de criação.
  const roots = useMemo(() => {
    return routeComments
      .filter((c) => c.is_root && !c.resolved)
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [routeComments]);

  const numberOf = useMemo(() => {
    const map = new Map<string, number>();
    roots.forEach((r, i) => map.set(r.id, i + 1));
    return map;
  }, [roots]);

  const positioned = roots.map((r) => ({ comment: r, pos: positionFor(r) }));
  const general = positioned.filter((p) => p.pos.kind === 'general');
  const placed = positioned.filter((p) => p.pos.kind !== 'general');

  const threadComments = (threadId: string): Comment[] =>
    routeComments
      .filter((c) => c.thread_id === threadId)
      .sort((a, b) => a.created_at.localeCompare(b.created_at));

  // Posição do popover da thread ativa. Busca em TODOS os comentários da rota
  // (inclusive resolvidos) — abrir uma thread resolvida pelo painel precisa
  // posicionar o popover para o botão "Reabrir" ficar acessível.
  let activePos: { x: number; y: number } | null = null;
  if (activeThreadId) {
    const activeRoot = routeComments.find((c) => c.id === activeThreadId && c.is_root);
    if (activeRoot) {
      const pos = positionFor(activeRoot);
      activePos =
        pos.kind === 'general'
          ? { x: 60, y: window.innerHeight - 140 }
          : { x: pos.x, y: pos.y };
    }
  }

  // Com o painel aberto, os pins ficam escondidos (o drawer é a visão em foco).
  if (drawerOpen) return null;

  return (
    <>
      <div className="bc-pin-layer" {...{ [UI_ATTR]: '' }}>
        {placed.map(({ comment, pos }) => (
          <button
            key={comment.id}
            type="button"
            className={
              'bc-pin' +
              (comment.id === activeThreadId ? ' bc-pin--active' : '') +
              (comment.thread_id === highlightThreadId ? ' bc-pin--highlight' : '')
            }
            style={{ left: pos.x, top: pos.y }}
            onClick={() => openThread(comment.id === activeThreadId ? null : comment.id)}
          >
            {numberOf.get(comment.id)}
          </button>
        ))}
      </div>

      {general.length > 0 && (
        <div className="bc-general-cluster" {...{ [UI_ATTR]: '' }}>
          <span className="bc-general-label">Comentários gerais da tela</span>
          {general.map(({ comment }) => (
            <button
              key={comment.id}
              type="button"
              className={
                'bc-pin' +
                (comment.id === activeThreadId ? ' bc-pin--active' : '') +
                (comment.thread_id === highlightThreadId ? ' bc-pin--highlight' : '')
              }
              onClick={() => openThread(comment.id === activeThreadId ? null : comment.id)}
            >
              {numberOf.get(comment.id)}
            </button>
          ))}
        </div>
      )}

      {activeThreadId && activePos && (
        <ThreadPanel
          comments={threadComments(activeThreadId)}
          author={author}
          viewportX={activePos.x}
          viewportY={activePos.y}
          onReply={(body, name) => submitReply(activeThreadId, body, name)}
          onResolve={resolveThread}
          onDelete={deleteComment}
          onClose={() => openThread(null)}
        />
      )}

      {draft && (
        <Composer
          draft={draft}
          author={author}
          onSubmit={(body, name) => submitNew(body, name, draft)}
          onCancel={exitCommentMode}
        />
      )}
    </>
  );
}
